/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import type { XLinkDto } from '@shared/types';
import { Tooltip } from '../layout/Tooltip';

interface CurrentAssignmentsProps {
  assignments: XLinkDto[];
  onRemove: (xlinkSetId: string) => void;
  onReassign?: (xlinkSetId: string) => void;
  /** Disable all action buttons (batch mode) */
  disabled?: boolean;
}

export function CurrentAssignments({ assignments, onRemove, onReassign, disabled = false }: CurrentAssignmentsProps) {
  if (assignments.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-700 text-sm">
        No taxonomy codes assigned yet. Use the filters below to assign one.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <h4 className="text-sm font-medium text-gray-700 mb-1 shrink-0">Current Assignments</h4>
      <div className="space-y-1 overflow-y-auto pr-1 flex-1 min-h-0">
        {assignments.map((a) => (
        <div key={a.setId} className="bg-green-50 border border-green-200 rounded px-2 py-1 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1 text-xs truncate">
              <span className="font-medium text-green-700">{a.taxonomyCode.code}</span>
              <span className="text-gray-400">—</span>
              <span className="text-gray-700 truncate">{a.taxonomyCode.displayName}</span>
              <span className="text-gray-400 shrink-0">{new Date(a.rowCreatedDate).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {onReassign && (
              <Tooltip text="Replace this assignment with a different taxonomy code" position="left">
                <button
                  onClick={() => onReassign(a.setId)}
                  disabled={disabled}
                  className="text-[11px] px-1.5 py-0.5 border border-blue-300 text-blue-500 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reassign
                </button>
              </Tooltip>
            )}
            <Tooltip text="Archive this assignment (version chain preserved)" position="left">
              <button
                onClick={() => onRemove(a.setId)}
                disabled={disabled}
                className="text-[11px] px-1.5 py-0.5 border border-red-300 text-red-500 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Remove
              </button>
            </Tooltip>
          </div>
        </div>
      ))}
      </div>
    </div>
  );
}
